// Högtidsteman (Erik 2026-10-21): a small seasonal touch that comes back by
// itself every year (spiders around Halloween, snow in December, …) and that
// every user can switch off in Inställningar → Utseende (User.hogtidsteman,
// mirrored to the HOGTID_COOKIE so the layout never queries the database).
//
// Pure calendar logic, shared by the server (layout, API route) and the client:
// no React, no Prisma. Dates are Stockholm calendar days. The periods are chosen
// for the school year: they end before the long breaks where possible
// (Halloween before höstlovet in week 44, snow through jullovet).

export const HOGTID_COOKIE = "vektr-hogtid";

/** Cookie "0" = switched off; anything else (missing included) = on. */
export function resolveHogtid(raw: string | null | undefined): boolean {
  return raw !== "0";
}

export type HogtidEffekt =
  | "spindlar"
  | "sno"
  | "hjartan"
  | "pi"
  | "semlor"
  | "kycklingar"
  | "kanelbullar"
  | "kladdkakor"
  | "lussekatter"
  | "blommor"
  | "flaggor";

export interface Hogtid {
  id: string;
  namn: string;
  /** What appears on screen, for the settings page ("Spindlar som kryper"). */
  beskrivning: string;
  effekt: HogtidEffekt;
  /** Inclusive [first, last] day in the given year, as [month, day] pairs;
   *  a period over the new year is listed as two periods. */
  perioder: (ar: number) => [Dag, Dag][];
}

/** [month 1–12, day 1–31] */
export type Dag = [number, number];

/** Easter Sunday (Anonymous Gregorian algorithm). */
export function pasksondag(ar: number): Dag {
  const a = ar % 19;
  const b = Math.floor(ar / 100);
  const c = ar % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const manad = Math.floor((h + l - 7 * m + 114) / 31);
  const dag = ((h + l - 7 * m + 114) % 31) + 1;
  return [manad, dag];
}

/** The day `n` days from [month, day] in `ar` (n may be negative). */
function forskjut(ar: number, [m, d]: Dag, n: number): Dag {
  const t = new Date(Date.UTC(ar, m - 1, d + n));
  return [t.getUTCMonth() + 1, t.getUTCDate()];
}

/** Monday of ISO week `v` in `ar` (week 1 is the week with 4 January). */
export function isoVeckansMandag(ar: number, v: number): Dag {
  const jan4 = new Date(Date.UTC(ar, 0, 4));
  const tillMandag = (jan4.getUTCDay() + 6) % 7;
  const t = new Date(Date.UTC(ar, 0, 4 - tillMandag + (v - 1) * 7));
  return [t.getUTCMonth() + 1, t.getUTCDate()];
}

/** In calendar order through the school year. The first match wins, so a
 *  single day (Lucia, fettisdagen, pi-dagen) is listed before the longer
 *  period it can fall inside (jul, alla hjärtans dag, påsk). */
export const HOGTIDER: Hogtid[] = [
  {
    id: "kanelbullens-dag",
    namn: "Kanelbullens dag",
    beskrivning: "Kanelbullar som singlar ner",
    effekt: "kanelbullar",
    perioder: () => [[[10, 4], [10, 4]]],
  },
  {
    id: "halloween",
    namn: "Halloween",
    beskrivning: "Spindlar som kryper över skärmen",
    effekt: "spindlar",
    // The last school days before höstlovet (week 44) up to Alla helgons dag.
    perioder: () => [[[10, 21], [11, 1]]],
  },
  {
    id: "kladdkakans-dag",
    namn: "Kladdkakans dag",
    beskrivning: "Kladdkakebitar som singlar ner",
    effekt: "kladdkakor",
    perioder: () => [[[11, 7], [11, 7]]],
  },
  {
    id: "lucia",
    namn: "Lucia",
    beskrivning: "Lussekatter som singlar ner",
    effekt: "lussekatter",
    perioder: () => [[[12, 13], [12, 13]]],
  },
  {
    id: "jul",
    namn: "Jul",
    beskrivning: "Snö som faller",
    effekt: "sno",
    // From the Monday of week 49 to nyårsdagen (Erik 2026-10-21).
    perioder: (ar) => [
      [isoVeckansMandag(ar, 49), [12, 31]],
      [[1, 1], [1, 1]],
    ],
  },
  {
    id: "fettisdagen",
    namn: "Fettisdagen",
    beskrivning: "Semlor som singlar ner",
    effekt: "semlor",
    perioder: (ar) => {
      const dag = forskjut(ar, pasksondag(ar), -47);
      return [[dag, dag]];
    },
  },
  {
    id: "alla-hjartans-dag",
    namn: "Alla hjärtans dag",
    beskrivning: "Hjärtan som stiger",
    effekt: "hjartan",
    perioder: () => [[[2, 10], [2, 14]]],
  },
  {
    id: "pi-dagen",
    namn: "Pi-dagen",
    beskrivning: "Pi-tecken som svävar",
    effekt: "pi",
    perioder: () => [[[3, 14], [3, 14]]],
  },
  {
    id: "pask",
    namn: "Påsk",
    beskrivning: "Kycklingar som går runt och pickar",
    effekt: "kycklingar",
    // The school week before stilla veckan (Monday–Friday, 13 to 9 days before
    // Easter Sunday): a school week in every municipality, whether påsklovet
    // is stilla veckan or the week after Easter (Erik 2026-10-21).
    perioder: (ar) => {
      const p = pasksondag(ar);
      return [[forskjut(ar, p, -13), forskjut(ar, p, -9)]];
    },
  },
  {
    id: "valborg",
    namn: "Valborg",
    beskrivning: "Vårblommor som singlar ner",
    effekt: "blommor",
    perioder: () => [[[4, 30], [4, 30]]],
  },
  {
    id: "nationaldagen",
    namn: "Nationaldagen",
    beskrivning: "Svenska flaggor som vajar ner",
    effekt: "flaggor",
    perioder: () => [[[6, 6], [6, 6]]],
  },
];

/** Today's calendar day in Stockholm. */
export function stockholmsDag(nu: Date): { ar: number; dag: Dag } {
  const delar = new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Europe/Stockholm",
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).formatToParts(nu);
  const tal = (typ: string) => Number(delar.find((p) => p.type === typ)?.value);
  return { ar: tal("year"), dag: [tal("month"), tal("day")] };
}

const ordning = ([m, d]: Dag) => m * 100 + d;

/** The holiday whose period contains `nu`, or null. */
export function aktuellHogtid(nu: Date): Hogtid | null {
  const { ar, dag } = stockholmsDag(nu);
  const idag = ordning(dag);
  return HOGTIDER.find((h) => h.perioder(ar).some(([fran, till]) => idag >= ordning(fran) && idag <= ordning(till))) ?? null;
}

const MANADER = ["jan", "feb", "mars", "april", "maj", "juni", "juli", "aug", "sep", "okt", "nov", "dec"];

/** "21 okt – 1 nov" / "14 mars", for the settings page (school year of `ar`). */
export function periodText(h: Hogtid, ar: number): string {
  const p = h.perioder(ar);
  const fran = p[0][0];
  const till = p[p.length - 1][1];
  const fmt = ([m, d]: Dag) => `${d} ${MANADER[m - 1]}`;
  return ordning(fran) === ordning(till) && p.length === 1 ? fmt(fran) : `${fmt(fran)} – ${fmt(till)}`;
}

/**
 * Exams, diagnoses, everything shown on the classroom projector, and every
 * print view — the Halloween spiders were printed into the Ma1 PDF backup
 * (2026-10-28). Print views: /utskrift (PDF backup), /larare/utskrift,
 * *-utskrift (kursplanering) and the student report.
 */
export function arHogtidUndantag(vag: string): boolean {
  const delar = vag.split("/");
  return (
    delar.some((d) => d === "utskrift" || d.endsWith("-utskrift")) ||
    /^\/klass\/[^/]+\/rapport(\/|$)/.test(vag) ||
    vag.startsWith("/prov") ||
    vag.startsWith("/mina-prov") ||
    vag.includes("/projektor") ||
    vag.includes("/lektion") ||
    vag.startsWith("/genomgang/") ||
    delar.includes("diagnos") ||
    delar.includes("sak-exam-browser")
  );
}
